import React, { useState, useEffect, useMemo } from 'react';
import { AssetTypeField } from '@/firebase/assetService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { getUserOwnerRolesForOrganization } from '@/utils/roleHelpers';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';

interface UserRolePickerProps {
  field: AssetTypeField;
  value?: string; // User ID
  onChange: (userId: string | undefined) => void;
  required?: boolean;
  disabled?: boolean;
}

interface User {
  id: string;
  name: string;
  email?: string;
}

export const UserRolePicker: React.FC<UserRolePickerProps> = ({
  field,
  value,
  onChange,
  required = false,
  disabled = false
}) => {
  const { currentOrganization } = useOrganization();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get role filter from field metadata
  const roleFilter = field.metadata?.roleFilter;
  const organizationType = currentOrganization?.industryType || null;
  
  useEffect(() => {
    if (roleFilter && currentOrganization?.id) {
      loadUsers();
    } else {
      setIsLoading(false);
    }
  }, [roleFilter, currentOrganization?.id]);
  
  const loadUsers = async () => {
    if (!currentOrganization?.id || !roleFilter) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const usersRef = collection(db, 'users');
      
      // Query users in this organization
      const orgIdQuery = query(usersRef, where('organizationId', '==', currentOrganization.id));
      const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', currentOrganization.id));
      
      const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
        getDocs(orgIdQuery),
        getDocs(orgIdsQuery)
      ]);
      
      const allUserDocs = new Map<string, any>();
      
      // Combine results and deduplicate
      orgIdSnapshot.forEach(doc => {
        allUserDocs.set(doc.id, { id: doc.id, ...doc.data() });
      });
      orgIdsSnapshot.forEach(doc => {
        allUserDocs.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      // Filter users by owner role
      const eligibleUsers: User[] = [];
      for (const userDoc of allUserDocs.values()) {
        const userOwnerRoles = getUserOwnerRolesForOrganization(userDoc, currentOrganization.id);
        if (userOwnerRoles.includes(roleFilter)) {
          eligibleUsers.push({
            id: userDoc.id,
            name: userDoc.name || userDoc.email || 'Unknown User',
            email: userDoc.email
          });
        }
      }
      
      // Sort by name
      eligibleUsers.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(eligibleUsers);
    } catch (error) {
      console.error('Error loading users for role picker:', error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const selectedUser = users.find(u => u.id === value);
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.name} {required && <span className="text-red-500">*</span>}
      </label>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="text-sm text-gray-500">
          No users found with {roleFilter} role
        </div>
      ) : (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          disabled={disabled}
          required={required}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
        >
          <option value="">Select {field.name}...</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.name} {user.email && `(${user.email})`}
            </option>
          ))}
        </select>
      )}
      {field.description && (
        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
      )}
    </div>
  );
};

